<template>
  <el-dialog :title="isEdit ? '编辑 MCP 服务' : '新增 MCP 服务'" :visible="visible"
             width="560px" v-dialogDrag :modal-append-to-body="false"
             @update:visible="$emit('update:visible', $event)">
    <el-form ref="form" :model="form" :rules="rules" label-width="90px">
      <el-form-item label="名称" prop="name">
        <el-input v-model="form.name" maxlength="64" placeholder="同归属下唯一" />
      </el-form-item>
      <el-form-item label="传输方式" prop="transport">
        <el-select v-model="form.transport" style="width: 200px">
          <el-option label="streamable_http" value="streamable_http" />
          <el-option label="sse" value="sse" />
        </el-select>
      </el-form-item>
      <el-form-item label="端点地址" prop="endpoint">
        <el-input v-model="form.endpoint" placeholder="http(s)://host:port/path（支持企业内网地址）" />
      </el-form-item>
      <el-form-item label="附加请求头">
        <div class="kv_rows">
          <div v-for="(row, i) in headerRows" :key="'h' + i" class="kv_row">
            <el-input v-model="row.k" placeholder="Header 名" size="small" />
            <el-input v-model="row.v" placeholder="值，支持 ${secret.X}" size="small" />
            <el-button type="text" size="small" class="danger_text" @click="headerRows.splice(i, 1)">删除</el-button>
          </div>
          <el-button type="text" size="small" icon="el-icon-plus" @click="headerRows.push({ k: '', v: '' })">添加请求头</el-button>
        </div>
      </el-form-item>
      <el-form-item label="密钥 Secrets">
        <div class="kv_rows">
          <div class="kv_tip">AES 加密存储；请求头中用 ${secret.X} 引用；编辑时值显示 *** 表示保留原密文</div>
          <div v-for="(row, i) in secretRows" :key="'s' + i" class="kv_row">
            <el-input v-model="row.k" placeholder="Key" size="small" />
            <el-input v-model="row.v" placeholder="值（编辑回填 *** 即保留）" size="small" />
            <el-button type="text" size="small" class="danger_text" @click="secretRows.splice(i, 1)">删除</el-button>
          </div>
          <el-button type="text" size="small" icon="el-icon-plus" @click="secretRows.push({ k: '', v: '' })">添加密钥</el-button>
        </div>
      </el-form-item>
    </el-form>
    <div slot="footer">
      <el-button @click="$emit('update:visible', false)">取 消</el-button>
      <el-button type="primary" :loading="submiting" @click="submitForm">确 定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { agentBody, mapToRows, rowsToMap } from '../utils'

// MCP 服务器新增/编辑弹窗：transport 仅 streamable_http/sse；编辑先 get 全量回填
export default {
  name: 'AiAgentMcpEdit',
  props: {
    visible: { type: Boolean, default: false },
    scope: { type: String, default: 'self' },
    urls: { type: Object, required: true },
    serverId: { type: String, default: null }
  },
  data() {
    return {
      form: { name: '', transport: 'streamable_http', endpoint: '' },
      headerRows: [],
      secretRows: [],
      secretOrigKeys: [],
      submiting: false,
      rules: {
        name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
        transport: [{ required: true, message: '请选择传输方式', trigger: 'change' }],
        endpoint: [
          { required: true, message: '请输入端点地址', trigger: 'blur' },
          { pattern: /^https?:\/\/.+/, message: '必须是合法的 http/https URL', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    isEdit() { return !!this.serverId }
  },
  methods: {
    reset() {
      this.form = { name: '', transport: 'streamable_http', endpoint: '' }
      this.headerRows = []
      this.secretRows = []
      this.secretOrigKeys = []
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    // KV 行：只填 value 未填 key 会被 rowsToMap 静默丢弃，提交前拦下提示
    validateKvRows() {
      if (this.headerRows.some(r => !r.k && r.v)) {
        this.$message({ type: 'warning', message: '「附加请求头」存在未填 Key 的行，请补全 Key 或删除该行' })
        return false
      }
      if (this.secretRows.some(r => !r.k && r.v)) {
        this.$message({ type: 'warning', message: '「密钥 Secrets」存在未填 Key 的行，请补全 Key 或删除该行' })
        return false
      }
      return true
    },
    submitForm() {
      this.$refs.form.validate((valid) => {
        if (!valid) return
        if (!this.validateKvRows()) return
        this.submiting = true
        const payload = {
          name: this.form.name,
          transport: this.form.transport,
          endpoint: this.form.endpoint,
          headers: rowsToMap(this.headerRows),
          secrets: rowsToMap(this.secretRows, this.secretOrigKeys)
        }
        if (this.isEdit) payload.server_id = this.serverId
        const url = this.isEdit ? this.urls.mcpEdit : this.urls.mcpAdd
        this.$tools.asyncPost(this, url, agentBody(payload)).then((res) => {
          if (res.data.code == 0) {
            this.$message({ type: 'success', message: res.data.msg || '保存成功' })
            this.$emit('update:visible', false)
            this.$emit('saved')
          }
          this.submiting = false
        }).catch((err) => {
          this.submiting = false
          if (err && err.isAxiosError) this.$message({ type: 'error', message: '请求失败，请稍后重试' })
        })
      })
    }
  },
  watch: {
    visible(v) {
      if (!v) return
      this.reset()
      if (this.isEdit) {
        this.$tools.asyncPost(this, this.urls.mcpGet, agentBody({ server_id: this.serverId })).then((res) => {
          if (res.data.code != 0) return
          const s = res.data.data
          this.form = { name: s.name || '', transport: s.transport || 'streamable_http', endpoint: s.endpoint || '' }
          this.headerRows = mapToRows(s.headers)
          this.secretRows = mapToRows(s.secrets_masked)
          this.secretOrigKeys = Object.keys(s.secrets_masked || {})
          this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
        }).catch(() => {})
      }
    }
  }
}
</script>

<style lang="less" scoped>
.kv_rows { width: 100%; }
.kv_row {
  display: flex;
  gap: 8px;
  margin-bottom: 6px;
  align-items: center;
}
.kv_tip { font-size: 12px; color: #909399; margin-bottom: 6px; }
.danger_text { color: #f56c6c; }
</style>
